/**
 * Canonical enum values.
 *
 * Every list here mirrors a CHECK constraint in the DDL (ARCHITECTURE §4) or a derived
 * value set (§4.2). The runtime tuples exist so Zod schemas, SQL guards and UI switches all
 * key off one source; the types are inferred from the tuples, never hand-written.
 */
/** `posts.post_type` CHECK (§4). */
export const POST_TYPES = [
    'original',
    'repost',
    'repost_with_comment',
    'article',
    'comment',
    'other',
];
/**
 * `posts.subtype` — an OPEN annotation, deliberately not a CHECK (§4, §7.4).
 *
 * The taxonomy of `post_type` stays closed: promoting a shape to a first-class type is a
 * migration. `subtype` makes a row self-describing meanwhile, so a shape the mapper recognises but
 * cannot type ("a link share", "a group post") is visible without a schema change. These are the
 * values the default plugins emit today; unknown values render verbatim.
 */
export const KNOWN_POST_SUBTYPES = ['newsletter', 'link_share', 'group_post'];
/** Accepted spelling of a subtype anywhere it crosses the wire (`subtypes=` filter, §5.2). */
export const POST_SUBTYPE_PATTERN = /^[a-z][a-z0-9_]{0,39}$/u;
/**
 * `posts.content_type` CHECK (§4).
 *
 * `repost` (v4): a pure repost's body belongs to the original author, so its content is typed as
 * "reposted content" rather than borrowing the original's format — the embedded card still shows
 * what that format is. Quote posts keep describing their own commentary.
 */
export const CONTENT_TYPES = [
    'text',
    'image',
    'multi_image',
    'video',
    'document',
    'article_link',
    'poll',
    'repost',
    'other',
];
/** `scrape_runs.status` CHECK (§4). */
export const RUN_STATUSES = [
    'queued',
    'running',
    'success',
    'partial',
    'failed',
    'mapper_error',
];
/** `scrape_run_stages.fetch_status` CHECK (§4). */
export const STAGE_FETCH_STATUSES = ['pending', 'success', 'failed', 'skipped'];
/** `scrape_run_stages.map_status` CHECK (§4). */
export const STAGE_MAP_STATUSES = ['pending', 'success', 'failed', 'skipped'];
/** `raw_payloads.map_status` CHECK (§4) — distinct value set from the stage map status. */
export const RAW_MAP_STATUSES = ['pending', 'mapped', 'failed'];
/**
 * `scrape_runs.trigger_source` CHECK (§4). `watch` is the synthetic run the watch→analytics ingest
 * port writes (§19.5) and the backfill it requests for an unseeded profile (§19.4).
 */
export const TRIGGER_SOURCES = [
    'manual',
    'own_schedule',
    'group_schedule',
    'catchup',
    'watch',
];
/** `profiles.schedule_mode` / `groups.schedule_mode` CHECK (§4). */
export const SCHEDULE_MODES = ['manual', 'daily', 'mon_fri', 'weekly', 'monthly'];
/**
 * Data kind a scraper plugin produces — the extension seam for future kinds (§7.1).
 *
 * `company` is the org-page analogue of `profile`: a different provider surface producing the same
 * `MappedProfile`, so ingest lands both in `profile_snapshots`. Which of the two details slots a
 * run uses is decided per `profiles.kind` at enqueue time (§6.3).
 */
export const SCRAPER_KINDS = ['profile', 'posts', 'company'];
/**
 * `profiles.kind` CHECK (§4) — what a tracked subject *is*.
 *
 * A company page is a `profiles` row like any other, so every downstream system keys on
 * `profiles.id` and needs no branch. The kind rides the DTOs purely so the UI can render a square
 * avatar and the details stage can pick the right scraper slot.
 */
export const PROFILE_KINDS = ['person', 'company'];
/** Provider-agnostic fetch failure classification (§7.1); `scrape_run_stages.error_class` minus `'mapper'`. */
export const SCRAPE_ERROR_CLASSES = [
    'auth',
    'quota',
    'provider_error',
    'network',
    'timeout',
];
/** `scrape_run_stages.error_class` CHECK-free column: fetch classes plus the mapper class (§4, §11.1). */
export const STAGE_ERROR_CLASSES = [...SCRAPE_ERROR_CLASSES, 'mapper'];
/** Classes that raise a persistent per-plugin banner (§7.1, §11.1). */
export const SCRAPER_BANNER_CLASSES = ['auth', 'quota'];
/** Config field types a plugin may declare; drives the settings form and the derived Zod schema. */
export const CONFIG_FIELD_TYPES = ['string', 'secret', 'number', 'boolean', 'enum'];
/** Performance tier of a multiplier (§5.3, §8.3). */
export const TIERS = ['low', 'neutral', 'notable', 'strong', 'viral'];
/** `baselines.window_kind` CHECK (§4, §8.1). */
export const WINDOW_KINDS = ['trailing_90d', 'last_50', 'insufficient'];
/** The four tracked engagement metrics — the filterable/sortable set (§5.2, §5.3). */
export const METRIC_KEYS = ['reactions', 'comments', 'shares', 'saves'];
/**
 * Metric keys plus the derived total used by aggregate sections (§8.4) — and the
 * `baselines.metric` CHECK set (§4): total engagement carries its own materialized baseline so
 * slot performance has a volume-neutral denominator (§8.6).
 */
export const AGGREGATE_METRIC_KEYS = [...METRIC_KEYS, 'totalEngagement'];
/** Which face a Schedule heatmap shows (§8.6); persisted per view in `ui_state` (§12.4). */
export const SCHEDULE_HEATMAP_MODES = ['volume', 'performance'];
/** `jobs.type` CHECK (§4). */
export const JOB_TYPES = ['remap', 'recompute_baselines'];
/** `jobs.status` CHECK (§4). */
export const JOB_STATUSES = ['queued', 'running', 'success', 'failed'];
/** `media_assets.owner_kind` CHECK (§4). */
export const MEDIA_OWNER_KINDS = ['post', 'profile'];
/** `media_assets.role` (§4, §9). */
export const MEDIA_ROLES = [
    'post_image',
    'document',
    'video_thumb',
    'embedded_image',
    'article_image',
    'avatar',
    'banner',
];
/** `media_assets.cache_status` CHECK (§4, §9.2). */
export const MEDIA_CACHE_STATUSES = ['uncached', 'cached', 'failed'];
/** Rendering family of a media asset, derived from role/content-type (§9.3 `MediaRefDTO.kind`). */
export const MEDIA_KINDS = ['image', 'video', 'document', 'other'];
/** State of a metrics-mode section (§5.1 #24, §8.10). */
export const SECTION_STATES = ['ok', 'empty'];
/** Derived profile status (§4.2) — never stored. */
export const PROFILE_STATUSES = [
    'never_scraped',
    'idle',
    'queued',
    'scraping',
    'error',
    'mapper_error',
];
/** Subject type of a view route (`:t` in §5.1). */
export const SUBJECT_TYPES = ['profile', 'group'];
/** View screen modes (§12.1). */
export const VIEW_MODES = ['metrics', 'posts', 'feed', 'profile'];
/** Time-series bucket granularity (§8.5). */
export const BUCKET_GRANULARITIES = ['day', 'week', 'month'];
/** Sort direction for list endpoints (§5.1 #25). */
export const SORT_DIRS = ['asc', 'desc'];
/** Length filter unit (§5.2). */
export const LENGTH_UNITS = ['words', 'chars'];
/** Export formats (§5.1 #29). */
export const EXPORT_FORMATS = ['csv', 'json'];
/** Scrape mode chosen by `buildScrapeContext` (§7.6). */
export const SCRAPE_MODES = ['backfill', 'incremental'];
/**
 * Provider-agnostic AI generation failure classification (SPEC `Comment Generation` §2) — the
 * `class` an `AiBackendError` carries.
 *
 * Deliberately a separate list from `SCRAPE_ERROR_CLASSES`: the two vocabularies answer different
 * questions (a scrape class drives the retry ladder and the plugin banner; an AI class drives one
 * HTTP status and one in-chat sentence), and a shared enum would tie a provider taxonomy to the
 * scheduler's. Anything a backend cannot classify becomes `provider_error`.
 */
export const AI_ERROR_CLASSES = [
    'auth',
    'rate_limit',
    'overloaded',
    'timeout',
    'invalid_request',
    'provider_error',
];
//# sourceMappingURL=enums.js.map